import styled from '@emotion/styled';
import UserEventTagList from 'presentations/views/components/molecules/UserEventTagList';
import { SharedEventDetailItem } from 'presentations/api/schema/event';
import Icon from 'presentations/views/components/atoms/Icon';
import Image from 'presentations/views/components/atoms/Image';
import { formatDateTimeRange } from 'presentations/views/helpers/formatter';
import { gray01, gray02, gray04 } from 'presentations/views/styles/color';
import {
  fontSize,
  fontWeight,
  spacing,
  mediaMax,
} from 'presentations/views/styles';
import { formatToISO } from 'infras/datetime';
import { splitHostAndUsersFromEvent } from 'presentations/views/helpers/event';
import { HostOperationType } from 'domains/models/event_type/hostOperationType';
import { useMemo } from 'react';

interface Props {
  event: SharedEventDetailItem;
  hostImage?: string | null;
  teamImage?: string | null;
}

export default function Component(props: Props) {
  const timeZone = props.event.timeZone!;
  const startedAt = new Date(formatToISO(props.event.startedAt, timeZone));
  const endedAt = formatToISO(props.event.endedAt, timeZone);
  const { host } = splitHostAndUsersFromEvent(props.event.users);

  const hostName = useMemo(() => {
    const name = host ? host.name : null;
    return name;
  }, [host]);

  const teamName = useMemo(() => {
    const name = props.event.team ? props.event.team.name : null;
    return name;
  }, [props.event.team]);

  return (
    <StyledContainer>
      {props.hostImage && (
        <StyledHostImageContainer>
          <StyledHostImage src={props.hostImage} alt={hostName ?? ''} />
        </StyledHostImageContainer>
      )}
      <StyledEventInfoContainer>
        <TagList>
          <UserEventTagList
            status={props.event.status}
            isHost={props.event.isHost}
          />
        </TagList>
        <Name>
          {props.event.hostOperationType === HostOperationType.Team ? (
            <HostName>{teamName}</HostName>
          ) : (
            <HostName>{hostName}</HostName>
          )}
          {props.event.hostOperationType === HostOperationType.None &&
            teamName && <TeamName>{teamName}</TeamName>}
        </Name>
        <PageName>{props.event.name}</PageName>
        <EventDate>
          {formatDateTimeRange(startedAt, endedAt, timeZone)}
        </EventDate>
        <SummaryList>
          <SummaryListItem>
            <ListItemIcon name="schedule" />
            <ListItemText>{props.event.timeZone}</ListItemText>
          </SummaryListItem>
        </SummaryList>
      </StyledEventInfoContainer>
      {props.teamImage && (
        <StyledTeamImageContainer>
          <StyledTeamImage src={props.teamImage} alt={teamName ?? ''} />
        </StyledTeamImageContainer>
      )}
    </StyledContainer>
  );
}

const StyledContainer = styled.div`
  display: flex;
`;
const StyledEventInfoContainer = styled.div`
  flex-grow: 1;
  margin-top: ${spacing(24)};
  margin-right: ${spacing(20)};

  ${mediaMax.tablet} {
    width: 100%;
    margin-right: 0;
  }
`;
const StyledHostImageContainer = styled.div`
  margin-top: ${spacing(24)};
  margin-right: ${spacing(16)};
`;
const StyledHostImage = styled(Image)`
  width: 136px;
  height: 136px;

  object-fit: cover;

  border-radius: 50%;

  ${mediaMax.tablet} {
    width: 50px;
    height: 50px;
  }
`;

const StyledTeamImageContainer = styled.div`
  height: 100%;
  margin: auto 0;
  display: flex;
  align-items: center;
  border-left: 1px solid ${gray04};
  padding-left: ${spacing(24)};
`;
const StyledTeamImage = styled(Image)`
  max-width: 120px;
  max-height: 60px;

  border-radius: 50%;

  ${mediaMax.tablet} {
    max-width: 60px;
    max-height: 30px;
  }
`;

const Name = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-bottom: ${spacing(4)};
  ${mediaMax.tablet} {
    flex-direction: column;
    align-items: flex-start;
    margin-bottom: ${spacing(6)};
  }
`;
const PageName = styled.div`
  font-size: ${fontSize(24)};
  font-weight: ${fontWeight('bold')};
  line-height: 1.2;
  margin-bottom: ${spacing(8)};
  ${mediaMax.tablet} {
    font-size: ${fontSize(20)};
  }
`;
const HostName = styled.div`
  font-size: ${fontSize(16)};
  font-weight: ${fontWeight('bold')};
  line-height: 1.2;
`;
const TeamName = styled.span`
  margin-left: ${spacing(16)};
  font-size: ${fontSize(13)};
  color: ${gray01};

  ${mediaMax.tablet} {
    margin-left: 0;
  }
`;
const EventDate = styled.div`
  margin-bottom: ${spacing(4)};
  font-size: ${fontSize(16)};
  font-weight: ${fontWeight('bold')};

  ${mediaMax.tablet} {
    font-size: ${fontSize(14)};
  }
`;

const SummaryList = styled.ul``;
const SummaryListItem = styled.li`
  color: ${gray02};
  display: flex;
  align-items: center;
  margin-bottom: ${spacing(16)};
  :last-of-type {
    margin-bottom: 0;
  }
`;
const ListItemIcon = styled(Icon)`
  margin-right: ${spacing(4)};
  color: ${gray02};
  width: 16px !important;
  height: 16px !important;
`;
const ListItemText = styled.span`
  color: ${gray02};
  font-size: ${fontSize(12)};
  vertical-align: middle;
  line-height: 1;
`;

const TagList = styled.div`
  margin-bottom: ${spacing(8)};
  ${mediaMax.tablet} {
    margin-bottom: 0;
  }
`;
